import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home, Briefcase, BarChart3, FileText, Users, Settings,
  ClipboardList, ChevronDown, ChevronLeft, ChevronRight,
  UserPlus, MessageSquare, Building2, X, User,ActivityIcon,
  Wallet,
  HandCoins,
  CalendarDays,
  Smartphone,
  Target,
  ClipboardCheck,
  Link2,
  Shield,
} from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { useTranslation } from "react-i18next";
import { getPermissions } from "@/lib/auth";
import { helper } from "@/lib/helper";

interface SubItem { title: string; url: string; icon: React.ElementType; match?: string; perm?: string;  }
interface NavItem { perm?: string;  title: string; url?: string; icon: React.ElementType; children?: SubItem[]; match?: string; }

export const getNavItems = (t: (key: string) => string): NavItem[] => [
  { title: t("nav.home"), url: "/home", icon: Home },//done

  { title: t("nav.customer_profile"), url: "/customer-profile", icon: User ,perm: "customers-index" },//done

  {
    title: t("nav.actions"),
    icon: Briefcase,
    children: [
      { title: t("nav.leads"), url: "/actions/leads", icon: UserPlus, perm: "view-lead" },//done

      { title: t("nav.new_customer"), url: "/actions/new-customer", icon: UserPlus, perm: "customers-add" },//done

      { title: t("nav.customers"), url: "/actions/customers/incomplete-profile", icon: Users, match: "/actions/customers" },

      { title: t("nav.loan_dues"), url: "/actions/due/today", icon: Wallet, match: "/actions/due" },

      { title: t("nav.loans"), url: "/actions/loans/co", icon: HandCoins, match: "/actions/loans" },

      { title: t("nav.expenses"), url: "/actions/expenses", icon: FileText ,perm: "view-expenses"},//done

      { title: t("nav.messenger"), url: "/actions/bulk-sms", icon: MessageSquare ,perm: "messenger" },//done
    ],
  },

  { title: t("nav.dashboard"), url: "/dashboard", icon: BarChart3 ,perm: "dashboard" },//done
{ title: t("nav.calendar"), url: "/calendar/collections", icon: CalendarDays, match: "/calendar" },
  
  { title: t("nav.mpesa_records"), url: "/mpesa/collections", icon: Smartphone, match: "/mpesa" },//done

  { title: t("nav.targets"), url: "/targets", icon: Target ,perm: "branch-target"},//management-reports

  { title: t("nav.reports"), url: "/reports", icon: ClipboardCheck ,perm: "reports"}, //done

  { title: t("nav.management_report"), url: "/management/monthly-performance", icon: ClipboardCheck, match: "/management",perm: "management-reports" },//done

  { title: t("nav.pairing"), url: "/pairing", icon: Link2 ,perm: "pair"},//done

  { title: t("nav.users"), url: "/users/all", icon: Shield, match: "/users" ,perm: "users"},//done

  { title: t("nav.settings"), url: "/settings/all", icon: Settings, match: "/settings" ,perm: "settings"},//done

  { title: t("nav.audit_trails"), url: "/audit-trails", icon: ClipboardList  ,perm: "audits"},//done
   { title: t("nav.activity"), url: "/activity", icon: ActivityIcon  },//done perm: "activity"
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

export function Sidebar({ collapsed, onToggle, mobileOpen, onMobileClose }: SidebarProps) {
  const location = useLocation();
  const { t, i18n } = useTranslation();



const navItems = useMemo(() => {
  const perms = new Set(getPermissions());
  const items = getNavItems(t);

  // pick best calendar URL based on permission priority
  const calendarUrl =helper. firstAllowedRoute(
    [
      { perm: "co-calendar", url: "/calendar/collections" },
      { perm: "dis-calendar", url: "/calendar/disbursement" },
    ],
    perms
  );

  // 🔥 Mpesa destination
  const mpesaUrl = helper.firstAllowedRoute(
    [
      { perm: "mpesa-records", url: "/mpesa/collections" },
      { perm: "unresolved-mpesa-records", url: "/mpesa/unresolved-collections" },
    ],
    perms
  );

  // update the existing Calendar item in place (and hide if none)
  const withCalendar = items
    .map((it) => {
      if (it.match === "/calendar" || it.url?.startsWith("/calendar")) {
        if (!calendarUrl) return null; // hide calendar if no perms
        return { ...it, url: calendarUrl }; // keep one calendar item
      }

      // Mpesa
      if (it.match === "/mpesa") {
        if (!mpesaUrl) return null;
        return { ...it, url: mpesaUrl };
      }

      return it;
    })
    .filter(Boolean) as NavItem[];

  // also filter by perm field (your earlier logic)
  return filterNavByPerm(withCalendar, Array.from(perms));
}, [t]);




  const { theme } = useTheme();

  // ✅ source of truth for RTL/LTR
  const dir = (document.documentElement.getAttribute("dir") as "ltr" | "rtl") || i18n.dir() || "ltr";
  const isRTL = dir === "rtl";

  const [openGroups, setOpenGroups] = useState<string[]>([]);

  const normalize = (path: string) => path.replace(/\/+$/, "");

  const isChildActive = (child: SubItem) => {
    const current = normalize(location.pathname);
    const m = normalize(child.match || child.url);
    return current === m || current.startsWith(m + "/");
  };

  const isNavActive = (item: NavItem) => {
    if (!item.url) return false;
    const current = normalize(location.pathname);
    const m = normalize(item.match || item.url);
    return current === m || current.startsWith(m + "/");
  };

  const isGroupActive = (children?: SubItem[]) => children?.some(isChildActive) ?? false;

  // ✅ Auto open the active group (but keep it stable)
 useEffect(() => {
  // Find the group (if any) that matches the current path
  const activeGroup = navItems.find((it) => it.children && isGroupActive(it.children));

  setOpenGroups(() => {
    // If route is inside a group, keep only that group open
    if (activeGroup) return [activeGroup.title];

    // If route is NOT inside any group, close all groups
    return [];
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [location.pathname, navItems]);

  const toggleGroup = (title: string) => {
    setOpenGroups((prev) =>
      prev.includes(title) ? prev.filter((x) => x !== title) : [...prev, title]
    );
  };

  const isDark = theme === "dark";

  const sidebarBase = isDark
    ? "bg-gradient-to-b from-[#1b1930] to-[#0b0a14] border-white/10 text-white"
    : "bg-gradient-to-b from-slate-50 to-slate-100 border-slate-200 text-slate-900";

  const logoText = isDark ? "text-white" : "text-slate-900";
  const mutedText = isDark ? "text-white/60 hover:text-white" : "text-slate-600 hover:text-slate-900";

  const itemBase = isDark ? "text-white hover:bg-white/10" : "text-slate-800 hover:bg-slate-200/60";
  const groupInactive = itemBase;

  const groupActive = isDark ? "bg-white/10 text-white" : "bg-slate-200 text-slate-900";

  const childInactive = isDark
    ? "text-white/70 hover:bg-white/10 hover:text-white"
    : "text-slate-600 hover:bg-slate-200/60 hover:text-slate-900";

  const primaryActive = isDark
    ? "bg-[#7C3AED] text-white shadow-[0_0_0_1px_rgba(124,58,237,0.35),0_8px_30px_rgba(124,58,237,0.18)]"
    : "bg-[#7C3AED] text-white shadow-[0_0_0_1px_rgba(124,58,237,0.25),0_10px_30px_rgba(124,58,237,0.12)]";

  // ✅ RTL-safe spacing
  const padX = isRTL ? "pr-3 pl-2" : "pl-3 pr-2";
  const childIndent = isRTL ? "mr-4" : "ml-4";
  const accentSide = isRTL ? "right-0" : "left-0";
  const logoGap = isRTL ? "mr-3" : "ml-3";

  // ✅ sidebar positioning + correct slide direction
const sidebarClasses = `
  fixed top-0 h-screen z-40 flex flex-col transition-all duration-300
  ${isRTL ? "right-0 border-l border-r-0" : "left-0 border-r"}
  ${sidebarBase}
  ${collapsed ? "lg:w-16 w-64" : "w-64"}
  ${mobileOpen ? "translate-x-0" : isRTL ? "translate-x-full" : "-translate-x-full"}
  lg:translate-x-0
`;

  return (
    <>
      {/* ✅ Mobile overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.button
            type="button"
            aria-label="Close sidebar"
            onClick={onMobileClose}
            className="fixed inset-0 z-30 bg-black/40 lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
        )}
      </AnimatePresence>

      <aside className={sidebarClasses} dir={dir}>
        {/* Logo */}
        <div className={`flex items-center justify-between h-16 px-4 border-b ${isDark ? "border-white/10" : "border-slate-200"}`}>
          <div className="flex items-center min-w-0">
            <Building2 className={`h-7 w-7 shrink-0 ${isDark ? "text-[#A78BFA]" : "text-[#7C3AED]"}`} />
            {!collapsed && (
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className={`${logoGap} text-lg font-bold truncate ${logoText}`}
              >
                LoanPro
              </motion.span>
            )}
          </div>

          <button onClick={onMobileClose} className={`lg:hidden ${mutedText}`}>
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-1">
          {navItems.map((item) => {
            if (item.children) {
              const groupOpen = openGroups.includes(item.title);
              const groupActiveNow = isGroupActive(item.children);

              return (
                <div key={item.title} className="space-y-1">
                  <button
                    onClick={() => (collapsed ? onToggle() : toggleGroup(item.title))}
                    className={`relative w-full flex items-center gap-3 ${padX} py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                      groupActiveNow ? groupActive : groupInactive
                    }`}
                  >
                    {/* premium accent */}
                    {groupActiveNow && (
                      <span className={`absolute ${accentSide} top-2 bottom-2 w-1 rounded-full bg-[#A78BFA]`} />
                    )}

                    <item.icon className="h-5 w-5 shrink-0" />
                    {!collapsed && (
                      <>
                        <span className="uppercase flex-1 text-start truncate">{item.title}</span>
                        <motion.span animate={{ rotate: groupOpen ? 180 : 0 }} transition={{ duration: 0.18 }}>
                          <ChevronDown className="h-4 w-4" />
                        </motion.span>
                      </>
                    )}
                  </button>

                  <AnimatePresence initial={false}>
                    {groupOpen && !collapsed && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className={`overflow-hidden ${childIndent} mt-1 space-y-0.5`}
                      >
                        {item.children.map((child) => (
                          <NavLink
                            key={child.url}
                            to={child.url}
                            onClick={onMobileClose}
                            className={`relative flex items-center gap-3 ${padX} py-2 rounded-xl text-sm transition-colors ${
                              isChildActive(child) ? primaryActive : childInactive
                            }`}
                          >
                            {isChildActive(child) && (
                              <span className={`absolute ${accentSide} top-2 bottom-2 w-1 rounded-full bg-white/70`} />
                            )}
                            <child.icon className="h-4 w-4 shrink-0" />
                            <span className="uppercase truncate">{child.title}</span>
                          </NavLink>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            }

            return (
              <NavLink
                key={item.title}
                to={item.url!}
                onClick={onMobileClose}
                className={`relative flex items-center gap-3 ${padX} py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                  isNavActive(item) ? primaryActive : itemBase
                }`}
              >
                {isNavActive(item) && (
                  <span className={`absolute ${accentSide} top-2 bottom-2 w-1 rounded-full bg-white/70`} />
                )}
                <item.icon className="h-5 w-5 shrink-0" />
                {!collapsed && <span className="uppercase truncate">{item.title}</span>}
              </NavLink>
            );
          })}
        </nav>

        {/* Collapse toggle */}
        <button
          onClick={onToggle}
          className={`hidden lg:flex items-center justify-center h-12 border-t transition-colors ${
            isDark ? "border-white/10 text-white/60 hover:text-white" : "border-slate-200 text-slate-600 hover:text-slate-900"
          }`}
          title={collapsed ? "Expand" : "Collapse"}
        >
          {collapsed ? (
            isRTL ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />
          ) : (
            isRTL ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />
          )}
        </button>
      </aside>
    </>
  );
  function filterNavByPerm(items: NavItem[], perms: string[]) {
  const set = new Set(perms);

  const canSee = (item: NavItem) => {
    if (!item.perm) return true; // no permission required
    return set.has(item.perm);
  };

  const walk = (list: NavItem[]): NavItem[] =>
    list
      .map((item) => {
        if (item.children?.length) {
          const children = walk(item.children);
          // show parent only if it has at least 1 visible child
          return children.length ? { ...item, children } : null;
        }

        return canSee(item) ? item : null;
      })
      .filter(Boolean) as NavItem[];

  return walk(items);
}
}